import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FileText, Eye, Calendar, User, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

function getViewerUrl(url) {
  if (!url) return null;

  const driveFileMatch = url.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (driveFileMatch) {
    return `https://drive.google.com/file/d/${driveFileMatch[1]}/preview?rm=minimal`;
  }

  const driveIdMatch = url.match(/drive\.google\.com\/(?:open|uc)[^?]*\?.*[?&]id=([a-zA-Z0-9_-]+)/);
  if (driveIdMatch) {
    return `https://drive.google.com/file/d/${driveIdMatch[1]}/preview?rm=minimal`;
  }

  return `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;
}

// Repeating diagonal watermark rendered over the iframe.
// pointer-events: none so the iframe stays scrollable/zoomable.
// Any screenshot the student takes will have their identity embedded.
function Watermark({ text }) {
  const items = Array.from({ length: 32 });
  return (
    <div
      aria-hidden
      style={{
        position: 'absolute', inset: 0, overflow: 'hidden',
        pointerEvents: 'none', userSelect: 'none', zIndex: 10,
      }}
    >
      {items.map((_, i) => (
        <span
          key={i}
          style={{
            position: 'absolute',
            left: `${(i % 4) * 26}%`,
            top: `${Math.floor(i / 4) * 14}%`,
            transform: 'rotate(-28deg)',
            opacity: 0.1,
            fontSize: '11px',
            fontWeight: 700,
            color: '#000',
            whiteSpace: 'nowrap',
            letterSpacing: '0.04em',
          }}
        >
          {text}
        </span>
      ))}
    </div>
  );
}

export default function NotesSection({ course }) {
  const notes = course?.notes || [];
  const [viewingNote, setViewingNote] = useState(null);

  // Watermark label: prefer name + email so the identity is unambiguous.
  const watermarkText = [course?.full_name, course?.email].filter(Boolean).join(' · ') || 'XceedEdge Student';

  const openViewer = (note) => {
    if (!note?.file_url) return;
    setViewingNote(note);
  };

  const closeViewer = () => setViewingNote(null);

  const blockContextMenu = (e) => e.preventDefault();

  return (
    <>
      <div className="space-y-6">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">Study Notes</h2>
          <p className="text-muted-foreground text-sm mt-1">
            {notes.length} {notes.length === 1 ? 'document' : 'documents'} available
          </p>
        </div>

        {notes.length === 0 ? (
          <div className="glass rounded-2xl border border-white/80 p-8 text-center">
            <p className="text-muted-foreground text-sm">No notes available yet.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {notes.map((note, i) => (
              <motion.div key={note.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
                whileHover={{ y: -3, transition: { duration: 0.2 } }}
                className="glass rounded-2xl border border-white/80 p-5 group hover:border-primary/30 transition-colors shadow-sm">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center mb-4">
                  <FileText className="w-6 h-6 text-foreground" />
                </div>

                <h3 className="font-heading font-bold text-sm leading-snug group-hover:text-primary transition-colors mb-1">{note.title}</h3>
                {note.module && <p className="text-xs text-muted-foreground mb-2">{note.module}</p>}
                {note.description && <p className="text-xs text-muted-foreground mb-3 line-clamp-2">{note.description}</p>}

                <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4">
                  {note.file_type && <span className="uppercase font-semibold text-primary">{note.file_type}</span>}
                  {note.instructor_name && (
                    <span className="flex items-center gap-1"><User className="w-3 h-3 text-primary" />{note.instructor_name}</span>
                  )}
                  {note.created_date && (
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3 text-primary" />
                      {new Date(note.created_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                    </span>
                  )}
                </div>

                <Button size="sm" variant="outline" disabled={!note.file_url}
                  onClick={() => openViewer(note)}
                  className="w-full gap-1.5 rounded-xl text-xs font-semibold border-black/15 hover:bg-primary/10 hover:border-primary/30">
                  <Eye className="w-3.5 h-3.5" /> View Notes
                </Button>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Inline viewer modal */}
      {viewingNote && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-2"
          onClick={closeViewer}
        >
          <div
            className="bg-white rounded-2xl w-full h-full flex flex-col shadow-2xl select-none"
            onClick={e => e.stopPropagation()}
            onContextMenu={blockContextMenu}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b shrink-0">
              <div className="min-w-0 mr-4">
                <h3 className="font-semibold text-sm truncate">{viewingNote.title}</h3>
                {viewingNote.module && <p className="text-xs text-muted-foreground">{viewingNote.module}</p>}
              </div>
              <button onClick={closeViewer} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors shrink-0">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Viewer + watermark + button blocker */}
            <div className="flex-1 overflow-hidden rounded-b-2xl" style={{ position: 'relative' }}>
              <iframe
                src={getViewerUrl(viewingNote.file_url)}
                className="w-full h-full border-0"
                title={viewingNote.title}
                allow="fullscreen"
                sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
              />
              <Watermark text={watermarkText} />
              {/* Blocks Google Drive's floating "open in new tab" button (top-right of iframe).
                  The button lives inside the cross-origin iframe so we can't remove it via CSS,
                  but a div stacked above the iframe at the same coordinates intercepts the click. */}
              <div
                style={{
                  position: 'absolute', top: 0, right: 0,
                  width: 72, height: 72,
                  zIndex: 30, pointerEvents: 'all', cursor: 'default',
                  background: 'transparent',
                }}
                onClickCapture={e => { e.stopPropagation(); e.preventDefault(); }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
